import * as recallApi from '@otter-ai/recall-client';
import { PATH_URL } from '#constants';
import { PartialLoginState, ValidAuthMethod, ValidAuthMethods } from '#types';
import {
  currentTimeZone,
  encode,
  getDeviceUUID,
  getFromLocalStorage,
  hmacSHA256,
  LOCAL_STORAGE_KEYS,
  removeFromLocalStorage,
  setInLocalStorage,
} from '#utils';

const methods = Object.values(ValidAuthMethods) as string[];

/**
 * Shared authentication function that performs the core login API call
 * without any post-login side effects like redirects, local storage updates, etc.
 *
 * This function is used by both useLogin and useVerifyAuth hooks to ensure
 * they stay in sync and use the same authentication logic.
 */
export function isValidAuthMethod(method: string): method is ValidAuthMethod {
  return methods.includes(method);
}

export function openOAuthUrl(url: string): void {
  // TODO(OTWEB-9069): Conditionally open in new tab or same tab based on device type
  // if (this.desktopDetectionService.isDesktop) {
  //   // For desktop, open in new tab
  //   const connected_apps_url = `${window.location.origin}/setting/connected_apps`;
  //   window.open(connected_apps_url, '_blank');
  // } else {
  //   // For web, open in same tab (original behavior)
  //   window.open(url, '_self');
  // }
  window.open(url, '_self');
}

/**
 * Create a signature for the email check API
 * @param email - The email to create a signature for
 * @returns The signature
 */
export async function createEmailCheckSignature(email: string) {
  const algorithm = process.env.NEXT_PUBLIC_CREATE_USER_SIGNIN_ALGORITHM ?? '';
  const ts = (Date.now() / 1000).toFixed(0);
  const params = new URLSearchParams();
  params.append('algorithm', algorithm);
  params.append('email', email);
  params.append('ts', ts);
  const message = decodeURIComponent(params.toString());
  const secret = process.env.NEXT_PUBLIC_CREATE_USER_SIGNIN_SECRET ?? '';
  const signature = await hmacSHA256(message, secret);
  return { signature, algorithm, ts };
}

export function getPartialLoginState(): PartialLoginState {
  const partialLoginState = getFromLocalStorage<PartialLoginState>(
    LOCAL_STORAGE_KEYS.PARTIAL_LOGIN_STATE,
    true,
  );

  return partialLoginState ?? {};
}

export function setPartialLoginState(state: Partial<PartialLoginState>): void {
  const currentState = getPartialLoginState() ?? {};
  const newState = { ...currentState, ...state };
  setInLocalStorage(LOCAL_STORAGE_KEYS.PARTIAL_LOGIN_STATE, newState, true);
}

export function resetPartialLoginState(): void {
  removeFromLocalStorage(LOCAL_STORAGE_KEYS.PARTIAL_LOGIN_STATE, true);
}

export function isSpeechUrl(url: string): boolean {
  return url.startsWith(`${PATH_URL.CONVERSATION_PAGE}/`);
}

/**
 * Makes the API call for authenticating a user with their email and password.
 *
 * @returns The response from the authentication API.
 */
export async function authenticateUserWithEmailAndPassword({
  email,
  password,
}: {
  email: string;
  password: string;
}) {
  const token = encode(`${email}:${password}`);
  const headers = { Authorization: `Basic ${token}` };

  const query = {
    username: email,
    device_uuid: getDeviceUUID(),
    tz_str: currentTimeZone,
  };

  const response = await recallApi.login({
    headers,
    query,
    body: undefined,
    throwOnError: true,
  });

  return response.data;
}

/**
 * Makes the API call for authenticating a user with their username and server token.
 * This is the React/Next.js equivalent of the Angular loginWithServerToken method.
 *
 * @returns The response from the authentication API.
 */
export async function authenticateUserWithServerToken({
  username,
  serverToken,
}: {
  username: string;
  serverToken: string;
}) {
  const token = encode(`${username}:${serverToken}`);
  const headers = { Authorization: `Basic ${token}` };

  const query = {
    username,
    device_uuid: getDeviceUUID(),
    tz_str: currentTimeZone,
  };

  const response = await recallApi.login({
    headers,
    query,
    body: undefined,
    throwOnError: true,
  });

  return response.data;
}
